import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { MakeApiCall } from "../apis";

const SYNC_IN_PROGRESS_STATUSES = new Set([
    "queued",
    "syncing",
    "processing",
    "in_progress",
    "running",
    "pending",
]);
const SYNC_STATUS_POLL_INTERVAL_MS = 3000;

const isSyncInProgress = (data) => {
    const status = String(data?.sync_status || data?.status || "").toLowerCase();
    return SYNC_IN_PROGRESS_STATUSES.has(status);
};

const normalizeSkuList = (skus) => {
    const list = Array.isArray(skus) ? skus : [skus];
    return [...new Set(list.filter(Boolean))];
};

/**
 * Drives automatic dashboard-optimized/sync after product updates (e.g. COGS)
 * and polls sync-status until complete.
 *
 * @param {object} options
 * @param {string} [options.token]
 * @param {boolean} options.isSpApiConnected
 * @param {boolean} options.canMutate
 * @param {() => void} [options.onSyncComplete] — refresh local data after background sync finishes
 */
export function useProductSyncTracking({
    token,
    isSpApiConnected,
    canMutate,
    onSyncComplete,
}) {
    const [syncingProducts, setSyncingProducts] = useState(false);
    const [syncPollTick, setSyncPollTick] = useState(0);

    const syncPollTimeoutRef = useRef(null);
    const confirmedSyncingRef = useRef(false);
    const assumeSyncingOnNextPollRef = useRef(false);
    const pendingSkusRef = useRef([]);
    const syncingProductsRef = useRef(false);
    const onSyncCompleteRef = useRef(onSyncComplete);

    useEffect(() => {
        onSyncCompleteRef.current = onSyncComplete;
    }, [onSyncComplete]);

    useEffect(() => {
        syncingProductsRef.current = syncingProducts;
    }, [syncingProducts]);

    useEffect(() => {
        return () => {
            if (syncPollTimeoutRef.current) clearTimeout(syncPollTimeoutRef.current);
        };
    }, []);

    const runSyncRequest = useCallback(async (skus) => {
        const skuList = normalizeSkuList(skus);
        if (!skuList.length || !canMutate || !isSpApiConnected) return false;

        try {
            const response = await MakeApiCall(
                "dashboard-optimized/sync",
                "post",
                { sku: skuList },
                true,
                null,
                token
            );
            if (response?.status) {
                setSyncingProducts(true);
                assumeSyncingOnNextPollRef.current = true;
                setSyncPollTick((tick) => tick + 1);
                return true;
            }
            message.error(response?.message || "Failed to sync product changes.");
            return false;
        } catch (error) {
            console.error("Product sync error:", error);
            message.error("Something went wrong while syncing product changes.");
            return false;
        }
    }, [canMutate, isSpApiConnected, token]);

    const triggerAutoSync = useCallback(async (skus) => {
        const skuList = normalizeSkuList(skus);
        if (!skuList.length || !canMutate || !isSpApiConnected) return;

        if (syncingProductsRef.current) {
            pendingSkusRef.current = normalizeSkuList([...pendingSkusRef.current, ...skuList]);
            return;
        }

        await runSyncRequest(skuList);
    }, [canMutate, isSpApiConnected, runSyncRequest]);

    const pollSyncStatus = useCallback(async () => {
        if (!isSpApiConnected) {
            setSyncingProducts(false);
            confirmedSyncingRef.current = false;
            return false;
        }

        const keepIfAlreadySyncing = assumeSyncingOnNextPollRef.current;
        if (keepIfAlreadySyncing) {
            assumeSyncingOnNextPollRef.current = false;
        }

        try {
            const response = await MakeApiCall(
                "dashboard-optimized/sync-status",
                "get",
                null,
                true,
                {},
                token
            );

            const apiInProgress = response?.status && response?.data
                ? isSyncInProgress(response.data)
                : false;

            if (apiInProgress) {
                confirmedSyncingRef.current = true;
            } else if (confirmedSyncingRef.current) {
                message.success("Sync complete.");
                confirmedSyncingRef.current = false;
                if (typeof onSyncCompleteRef.current === "function") {
                    onSyncCompleteRef.current();
                }

                const pending = normalizeSkuList(pendingSkusRef.current);
                pendingSkusRef.current = [];
                if (pending.length > 0) {
                    await runSyncRequest(pending);
                    return true;
                }
            }

            const nextSyncing = apiInProgress || keepIfAlreadySyncing;
            setSyncingProducts(nextSyncing);
            return nextSyncing;
        } catch (error) {
            console.error("Product sync status error:", error);
            return false;
        }
    }, [isSpApiConnected, token, runSyncRequest]);

    useEffect(() => {
        if (!isSpApiConnected) {
            setSyncingProducts(false);
            confirmedSyncingRef.current = false;
            pendingSkusRef.current = [];
            return;
        }

        let cancelled = false;

        const runPoll = async () => {
            if (cancelled) return;

            const inProgress = await pollSyncStatus();
            if (cancelled) return;

            if (inProgress) {
                syncPollTimeoutRef.current = setTimeout(runPoll, SYNC_STATUS_POLL_INTERVAL_MS);
            }
        };

        runPoll();

        return () => {
            cancelled = true;
            if (syncPollTimeoutRef.current) {
                clearTimeout(syncPollTimeoutRef.current);
                syncPollTimeoutRef.current = null;
            }
        };
    }, [isSpApiConnected, pollSyncStatus, syncPollTick]);

    return {
        syncingProducts,
        triggerAutoSync,
    };
}
